import { useState } from "react";
import type { DeliveryPolicyReason } from "@reminder-tracker/backend-contracts";

type Mode = "disabled" | "test-only";
type Review = Readonly<{ mode: Mode; revision: number | null }>;
const label = (mode: Mode): string => (mode === "disabled" ? "Disabled" : "Test only");

export function ChannelPolicyReview({
  channel,
  environment,
  currentMode,
  revision,
  pendingMode,
  onConfirm,
  canManagePolicy,
}: Readonly<{
  channel: "Email" | "Android push" | "iOS push";
  environment: string;
  currentMode: Mode | null;
  revision: number | null;
  pendingMode: Mode | null;
  onConfirm: (mode: Mode, reason: DeliveryPolicyReason) => void;
  canManagePolicy: boolean;
}>): React.JSX.Element {
  const [review, setReview] = useState<Review | null>(null);
  const [reason, setReason] = useState<DeliveryPolicyReason | "">("");
  const stale = review !== null && review.revision !== revision;
  const channelLabel = channel === "iOS push" ? channel : channel.toLowerCase();
  const alternativeMode: Mode = currentMode === "test-only" ? "disabled" : "test-only";
  return (
    <div>
      {!canManagePolicy ? <p>Policy changes require the delivery policy permission.</p> : null}
      <div className="policy-current-state">
        <p>
          <strong>Current sending policy:</strong>{" "}
          {currentMode === null ? "Not configured" : label(currentMode)}
        </p>
        <button
          className="secondary-action"
          type="button"
          disabled={!canManagePolicy || pendingMode !== null}
          onClick={() => {
            setReason("");
            setReview({ mode: alternativeMode, revision });
          }}
        >
          {pendingMode === alternativeMode
            ? "Saving…"
            : `Review change to ${label(alternativeMode)}`}
        </button>
      </div>
      {review === null ? null : (
        <section aria-label={`Review ${channelLabel} policy change`}>
          <h3>Review {channelLabel} change</h3>
          <p>
            {environment}: change {channelLabel} from{" "}
            {currentMode === null ? "Not configured" : label(currentMode)} to {label(review.mode)}.
          </p>
          <p>
            {review.mode === "disabled"
              ? "New external requests will stop. Requests already in flight may still arrive."
              : "Only eligible synthetic test requests are permitted. Consent, verification and recipient controls still apply."}
          </p>
          <p>
            In-app reminders stay available. Existing reminders and history are unchanged. This
            action does not replay missed notifications.
          </p>
          <label className="delivery-policy-reason">
            Reason for this policy change
            <select
              value={reason}
              onChange={(event) => {
                const value = event.currentTarget.value;
                if (
                  value === "" ||
                  value === "planned-testing" ||
                  value === "testing-complete" ||
                  value === "incident-containment" ||
                  value === "verified-recovery"
                )
                  setReason(value);
              }}
            >
              <option value="">Choose a reason</option>
              <option value="planned-testing">Planned synthetic testing</option>
              <option value="testing-complete">Testing complete</option>
              <option value="incident-containment">Contain a delivery incident</option>
              <option value="verified-recovery">Recovery verified</option>
            </select>
          </label>
          {stale ? (
            <p role="alert">
              The policy changed after this review opened. Cancel and review the current policy
              before confirming.
            </p>
          ) : null}
          <div className="decision-actions">
            <button
              className={review.mode === "disabled" ? "caution-action" : "primary-action"}
              type="button"
              disabled={!canManagePolicy || stale || pendingMode !== null || reason === ""}
              onClick={() => {
                if (!canManagePolicy || review.revision !== revision || reason === "") return;
                onConfirm(review.mode, reason);
                setReview(null);
              }}
            >
              Confirm {channelLabel} change
            </button>
            <button
              className="secondary-action"
              type="button"
              disabled={pendingMode !== null}
              onClick={() => setReview(null)}
            >
              Keep current policy
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
